import { supportChamber } from './support';
import type { ApiErrorBody, ErrorCode } from '@provenance/shared';

/**
 * The API client.
 *
 * Deliberately small and hand-written rather than generated. Three things it has to do
 * that a generated client would not do for free:
 *
 *   1. REFRESH TRANSPARENTLY. An access token lives 15 minutes. An operator halfway
 *      through reviewing a certificate must not be thrown to the login screen — so a 401
 *      triggers one refresh and one retry, and concurrent requests share the single
 *      in-flight refresh rather than each starting their own (which would trip the
 *      server's reuse detection and revoke the whole family).
 *
 *   2. SURFACE THE SERVER'S ERROR CODE. The API names the specific failed check; the UI
 *      shows the server's message rather than inventing its own, because the server's is
 *      the one that is accurate.
 *
 *   3. FAIL LOUDLY ON A NON-JSON RESPONSE. A proxy error page parsed as JSON produces an
 *      unreadable crash far from the cause.
 */

export const ACCESS_KEY = 'pv.access';
export const REFRESH_KEY = 'pv.refresh';

/**
 * Who an access token belongs to, read without verifying it — only to tell whether another
 * tab has signed in as someone else. Never used to decide access; the API does that.
 */
export function subjectOf(accessToken: string | null): string | null {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split('.')[1] ?? '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { sub?: string }).sub ?? null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | string,
    message: string,
    readonly details?: Array<{ path: string; message: string }>,
    readonly context?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level errors keyed by path, for handing straight to react-hook-form. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details ?? []) out[d.path] = d.message;
    return out;
  }
}

export const tokens = {
  get access(): string | null {
    try {
      return localStorage.getItem(ACCESS_KEY);
    } catch {
      return null;
    }
  },
  get refresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  set(access: string, refresh: string): void {
    try {
      localStorage.setItem(ACCESS_KEY, access);
      localStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      // Private browsing, or storage disabled. The session then lasts until reload,
      // which is degraded but usable — better than a hard failure at login.
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* see above */
    }
  },
};

/** The chamber a support officer chose, on every request (D-031). Ignored for everyone else. */
function chamberHeader(): Record<string, string> {
  const chamber = supportChamber();
  return chamber ? { 'X-Provenir-Chamber': chamber.id } : {};
}

/** Shared across concurrent 401s, so only one refresh is ever in flight in this tab. */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the refresh token for a new session.
 *
 * Tabs share one session, so they must not refresh at the same moment: the second would
 * present a token the first had just rotated, and the server used to read that as theft and
 * end the session everywhere (T-140). So refreshing takes a browser-wide lock, and a tab that
 * waited for it first checks whether its neighbour already refreshed. The server's grace
 * window (`refresh_token_rotated`) covers browsers without the Web Locks API.
 *
 * `failedWith` is the access token the 401 came back for.
 */
async function refreshSession(failedWith: string | null): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const run = async (): Promise<boolean> => {
    // Another tab refreshed while this one waited.
    if (tokens.access && tokens.access !== failedWith) return true;
    const refreshToken = tokens.refresh;
    if (!refreshToken) return false;
    try {
      const res = await fetch('/api/console/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        const code = ((await res.json().catch(() => null)) as ApiErrorBody | null)?.error?.code;
        // Rotated a moment ago by another tab: use what it stored.
        return code === 'refresh_token_rotated' && tokens.refresh !== refreshToken;
      }
      const body = (await res.json()) as { accessToken: string; refreshToken: string };
      tokens.set(body.accessToken, body.refreshToken);
      return true;
    } catch {
      return false;
    }
  };

  const inFlight: Promise<boolean> = (async () =>
    typeof navigator !== 'undefined' && navigator.locks
      ? await navigator.locks.request('pv-session-refresh', run)
      : await run())().finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = inFlight;
  return inFlight;
}

export type OnUnauthenticated = () => void;
let onUnauthenticated: OnUnauthenticated = () => undefined;
export function setUnauthenticatedHandler(handler: OnUnauthenticated): void {
  onUnauthenticated = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Login and refresh must not recurse through the refresh path. */
  skipAuth?: boolean;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  let sentWith: string | null = null;
  const send = async (): Promise<Response> => {
    sentWith = tokens.access;
    return fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.skipAuth || !tokens.access ? {} : { Authorization: `Bearer ${tokens.access}` }),
        ...chamberHeader(),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  };

  let res = await send();

  if (res.status === 401 && !options.skipAuth) {
    if (await refreshSession(sentWith)) {
      res = await send();
    } else {
      tokens.clear();
      onUnauthenticated();
      throw new ApiError(401, 'token_invalid', 'Your session has ended. Sign in again.');
    }
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await res.text().catch(() => '');
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'internal_error',
        `The server returned ${res.status} and something that is not JSON. ${text.slice(0, 160)}`,
      );
    }
    return undefined as T;
  }

  const body = await res.json();

  if (!res.ok) {
    const error = (body as ApiErrorBody).error;
    throw new ApiError(
      res.status,
      error?.code ?? 'internal_error',
      error?.message ?? 'Something went wrong.',
      error?.details,
      error?.context,
      error?.requestId,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * A binary response — the stamping proof, which is a PDF.
   *
   * Separate from `request` rather than another branch inside it, because the two differ in
   * what a non-JSON body MEANS: everywhere else it is a surprise to be reported, here it is
   * the whole point. Shares the auth header and the 401-refresh, which is why an `<iframe
   * src>` pointed straight at the endpoint will not do — an iframe cannot send one.
   */
  blob: async (path: string, body?: unknown): Promise<Blob> => {
    // A POST when there is a body: the certificate preview has to send what the operator
    // currently has in the Approve dialog, which is far too much to put in a query string.
    let sentWith: string | null = null;
    const send = () => {
      sentWith = tokens.access;
      return fetch(new URL(path, window.location.origin).toString(), {
        method: body === undefined ? 'GET' : 'POST',
        headers: {
          ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
          ...chamberHeader(),
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    };

    let res = await send();
    if (res.status === 401) {
      if (await refreshSession(sentWith)) {
        res = await send();
      } else {
        tokens.clear();
        onUnauthenticated();
        throw new ApiError(401, 'token_invalid', 'Your session has ended. Sign in again.');
      }
    }

    if (!res.ok) {
      // The server reports a refusal as JSON even on an endpoint that normally returns a
      // PDF, so read it as such rather than handing back an unreadable blob.
      const body = await res.json().catch(() => null);
      const error = (body as ApiErrorBody | null)?.error;
      throw new ApiError(
        res.status,
        error?.code ?? 'internal_error',
        error?.message ?? `The server returned ${res.status}.`,
      );
    }
    return res.blob();
  },
  /** Login and refresh: no bearer token, no refresh-on-401 recursion. */
  anon: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body, skipAuth: true }),
};
